import type { SectionProps } from 'sections/types';

/**
 * Gallery Section Data Model
 * 
 * Pure data structure for listing image gallery.
 * No presentation logic - just data.
 */
export interface GallerySectionData {
  /** Image URLs */
  images: string[];
  
  /** Image descriptions for accessibility */
  descriptions?: Map<string, string> | null;
  
  /** Total image count */
  total: number;
  
  /** Badge overlay (optional) */
  badges?: React.ReactNode;
  
  /** Alt text prefix (e.g., address) */
  altPrefix?: string;
}

/**
 * Gallery Section Variants
 */
export const GALLERY_VARIANTS = {
  CAROUSEL: 'carousel',
  BENTO_GRID: 'bento-grid',
  COMPACT: 'compact',
} as const;

export type GalleryVariant = typeof GALLERY_VARIANTS[keyof typeof GALLERY_VARIANTS];

/**
 * Gallery Section Props
 */
export interface GallerySectionProps extends SectionProps {
  /** Section data */
  data: GallerySectionData;
  
  /** Layout variant */
  variant: GalleryVariant;
  
  /** Callback when image is clicked */
  onImageClick?: (index: number) => void;
  
  /** Callback when slide changes (carousel only) */
  onSlideChange?: (index: number) => void;
  
  /** Callback when "See All Photos" is clicked (bento grid only) */
  onSeeAllClick?: () => void;
  
  /** Whether to blur images (compliance) */
  blur?: boolean;
  
  /** Compliance overlay data (section renders it internally) */
  complianceOverlay?: {
    message: string;
    ctaText: string;
    onClick?: () => void;
  };
  
  /** Share data for carousel share button (carousel only) */
  share?: {
    text: string;
    url: string;
    listingId: number;
  };
}
